// Widget: "Was ist neu" — Changelog-Feed (InfoCenter) mit Ungelesen-Badge.
//
// Rendert nur den Inhalt (kein eigenes WidgetCard/Titel — das übernimmt der
// Dashboard-Grid-Wrapper), analog zu TodayWidget.tsx.

import { useEffect, useState } from "preact/hooks";
import { Sparkles, Bug, Wrench, Megaphone, type LucideIcon } from "lucide-preact";
import type { InfoEntry, InfoEntryType } from "@juuplan/shared";
import { fetchInfoEntries, postInfoSeen } from "../../lib/api";

// Anzahl der geladenen Einträge (neu → alt). Server clamped auf MAX_LIMIT=50,
// 45 liegt darunter → kein Backend-Change nötig.
const WHATS_NEW_LIMIT = 45;

interface TypeMeta {
  label: string;
  Icon: LucideIcon;
  color: string;
}

const TYPE_META: Record<InfoEntryType, TypeMeta> = {
  feature: { label: "Neu", Icon: Sparkles, color: "text-[color:var(--color-primary)]" },
  bugfix: { label: "Behoben", Icon: Bug, color: "text-[color:var(--color-error)]" },
  improvement: { label: "Verbessert", Icon: Wrench, color: "text-[color:var(--color-warning)]" },
  announcement: { label: "Info", Icon: Megaphone, color: "text-[color:var(--color-tertiary)]" },
};

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function WhatsNewWidget(): preact.JSX.Element {
  const [entries, setEntries] = useState<InfoEntry[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchInfoEntries(WHATS_NEW_LIMIT).then(
      (res) => {
        if (cancelled) return;
        setEntries(res.entries);
        setUnread(res.unread_count);
        setLoading(false);
      },
      () => {
        // Sonst blieb ein Ladefehler unsichtbar hinter "Keine Neuigkeiten." —
        // ein leerer Erfolg sieht identisch aus wie ein Fehlschlag, ohne
        // diesen eigenen Zustand.
        if (cancelled) return;
        setError(true);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // Markiert den Feed serverseitig als gesehen, sobald Einträge angezeigt
  // werden. Idempotent (Backend toleriert Mehrfachaufrufe); die lokal
  // angezeigte Badge-Zahl bleibt für diese Session unverändert, da sie den
  // Stand zum Zeitpunkt des Ladens widerspiegelt.
  useEffect(() => {
    if (loading || entries.length === 0) return;
    void postInfoSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, entries.length > 0]);

  if (loading)
    return <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>;
  if (error)
    return (
      <p class="text-xs text-[color:var(--color-error)] italic">
        Konnte nicht geladen werden.
      </p>
    );
  if (entries.length === 0)
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
        Keine Neuigkeiten.
      </p>
    );

  return (
    <div class="flex flex-col gap-2">
      {unread > 0 && (
        <span class="self-start rounded-full bg-[color:var(--color-primary)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-primary)]">
          {unread} neu
        </span>
      )}
      {/* ~6 Einträge sichtbar (je ≈ 3.5rem inkl. gap), Rest scrollt in der Box.
          overscroll-contain verhindert Scroll-Chaining aufs Dashboard. */}
      <ul class="flex flex-col gap-2 text-sm max-h-[22rem] overflow-y-auto overscroll-contain pr-1">
        {entries.map((entry) => {
          const meta = TYPE_META[entry.type];
          const date = formatDate(entry.published_at);
          return (
            <li key={entry.id} class="flex items-start gap-2">
              <meta.Icon size={14} class={`shrink-0 mt-0.5 ${meta.color}`} />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span class={`text-label font-medium uppercase tracking-wide ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span class="font-medium text-[color:var(--color-on-surface)] truncate">
                    {entry.title}
                  </span>
                </div>
                {entry.description && (
                  <p class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
                    {entry.description}
                  </p>
                )}
                {date && (
                  <p class="text-label text-[color:var(--color-on-surface-variant)]">{date}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
